/* eslint-disable no-unused-vars */
import { Component, ComponentArgs } from "@kluntje/core";
import { DebuggerService, URLSearchParamsService } from "@kluntje/services";
import { v4 as uuidv4 } from "uuid";
import { ERROR, DISABLED, ACTIVE, SUCCESS } from "Constants/cssClasses";
import { addClass, removeClass, toggleClass, waitForEvent } from "@kluntje/js-utils/lib/dom-helpers";
import { GENERIC_INIT_EVENT } from "Constants/eventTypes";

import { FormFieldSubmitValue } from "./sde-e-form-field.types";

export class FormField<SubmitType = string> extends Component {
  private formFieldInitPromise?: Promise<void>;

  protected uuid = uuidv4();

  constructor({ ui = {}, events = [], initialStates = {}, reactions = {} }: ComponentArgs = {}) {
    super({
      ui: {
        input: ".sde-e-form-field__input :-one",
      },
      events: [
        {
          event: "focusin",
          handler: "handleFocusIn",
          target: "input",
        },
        {
          event: "focusout",
          handler: "handleFocusOut",
          target: "input",
        },
      ],
      asyncRendering: true,
      initialStates: {
        showError: false,
        inValidationMode: false,
      },
      reactions: {
        showError: ["onErrorChange"],
        inValidationMode: ["onValidationModeChange"],
      },
    });

    Object.assign(this.ui, ui);
    Object.assign(this.initialStates, initialStates);
    Object.assign(this.reactions, reactions);

    this.mergeEvents(events);
  }

  protected get DEFAULT_ERROR_MESSAGE(): string {
    return "Bitte füllen Sie dieses Feld aus.";
  }

  public get name(): string {
    return this.getAttribute("name") || "";
  }

  public get autocompleteType(): string | undefined {
    return this.getAttribute("autocomplete") || undefined;
  }

  public get label(): string {
    return this.getAttribute("label") || "";
  }

  protected get placeholder(): string {
    return this.getAttribute("placeholder") || "";
  }

  protected get errorMessage(): string {
    return this.getAttribute("error-message") || this.DEFAULT_ERROR_MESSAGE;
  }

  protected get description(): string {
    return this.getAttribute("description") || "";
  }

  public get isRequired(): boolean {
    const attrName = "required";
    return this.hasAttribute(attrName) && this.getAttribute(attrName) !== "false";
  }

  public get isReadonly(): boolean {
    return this.hasAttribute("readonly") && this.getAttribute("readonly") !== "false";
  }

  public get isDisabled(): boolean {
    return this.hasAttribute("disabled") && this.getAttribute("disabled") !== "false";
  }

  public set isDisabled(newValue: boolean) {
    if (newValue === true) {
      this.setAttribute("disabled", "");
    } else {
      this.removeAttribute("disabled");
    }
  }

  protected get submitValueType(): string {
    return "string";
  }

  public get submitValue(): SubmitType | undefined {
    if (this.ui.input.value === undefined) return undefined;
    if (this.ui.input.value === "") return undefined;

    return this.ui.input.value;
  }

  public get submitData(): FormFieldSubmitValue<SubmitType> | null {
    if (this.name === "" || this.submitValue === undefined) return null;

    return {
      name: this.name,
      type: this.submitValueType,
      value: this.submitValue,
    };
  }

  public get submitString(): string | undefined {
    DebuggerService.error("FormField", "submitString() is not implemented");
    return "";
  }

  protected get prefillDisabled(): boolean {
    return this.hasAttribute("prefill-disabled") && this.getAttribute("prefill-disabled") !== "false";
  }

  protected get prefillName(): string | null {
    return this.getAttribute("prefill-name");
  }

  protected get prefillValue() {
    if (this.prefillDisabled) return null;
    const prefillName = this.prefillName || this.name;
    if (prefillName === "") return null;
    return URLSearchParamsService.get(prefillName);
  }

  static get observedAttributes() {
    return ["disabled"];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    this.handleAttributeChange(name, oldValue, newValue);
  }

  protected async handleAttributeChange(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue || !this.state.initialized) return;
    await this.reRenderHook();
    if (name === "disabled") toggleClass(this, DISABLED, this.isDisabled);
  }

  protected afterComponentRender() {
    this.formFieldInitPromise = this.initFormField();
  }

  protected async initFormField() {
    await this.prefillInput();
    this.dispatchEvent(new CustomEvent(GENERIC_INIT_EVENT, { bubbles: false }));
  }

  public async waitForFormFieldInit(timeout?: number): Promise<void> {
    if (this.formFieldInitPromise === undefined) {
      await waitForEvent(this, GENERIC_INIT_EVENT, timeout);
    }
    await this.formFieldInitPromise;
  }

  protected async prefillInput() {
    const prefillValue = this.prefillValue;
    if (prefillValue === null) return;
    this.ui.input.value = prefillValue;
    this.setState({ inValidationMode: true });
    await this.reRenderHook();
  }

  /**
   * Lifecycle method to be called when the component is rerendered. Should be overridden, when re-rendering is not done by renderAsync()
   */
  protected async reRenderHook() {
    await this.renderAsync();
  }

  /**
   * Central method to get the current validity-state of the form-field (can be overridden)
   * @returns {boolean}
   */
  public async isValid(): Promise<boolean> {
    await Promise.resolve();
    return this.ui.input.checkValidity();
  }

  /**
   * Validates this form-field by updating show error state
   */
  public async validate(): Promise<void> {
    const isValid = await this.isValid();
    this.setState({ showError: !isValid, inValidationMode: true });
    toggleClass(this, SUCCESS, isValid);
  }

  /**
   * Handles showError-state changes by re-rendering the component
   * @param { showError } state
   */
  protected async onErrorChange({ showError }: any): Promise<void> {
    toggleClass(this, ERROR, showError);
    await this.reRenderHook();
  }

  /**
   * Handles showError-state changes by re-rendering the component
   * @param { inValidationMode } state
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async onValidationModeChange({ inValidationMode }: any) {
    await Promise.resolve();
    // can be overridden by extender later
  }

  protected async handleFocusIn() {
    await Promise.resolve();
    addClass(this, ACTIVE);
  }

  protected async handleFocusOut() {
    await Promise.resolve();
    removeClass(this, ACTIVE);
  }

  public async reset() {
    this.setState({ showError: false, inValidationMode: false });
    this.ui.input.value = "";
    removeClass(this, SUCCESS);
    await this.prefillInput();
  }
}
